import React, { createContext, useEffect, useState } from 'react';
import type { AuthError, Session, User } from '@supabase/supabase-js';
import supabase from '@/lib/supabase.ts';
import { getBackofficeUser } from '@/features/backoffice-users';
import { toast } from 'react-toastify';
import type { BackofficeUser } from '@/features/backoffice-users';

type UserContextType = {
  user: User | null;
  session: Session | null;
  backofficeUser: BackofficeUser | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refreshBackofficeUser: () => Promise<void>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [backofficeUser, setBackofficeUser] = useState<BackofficeUser | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  // Funzione per recuperare i dettagli dell'utente backoffice
  const loadBackofficeUser = async (
    userId: string,
  ): Promise<BackofficeUser | null> => {
    try {
      return await getBackofficeUser(userId);
    } catch (error) {
      console.error('Errore nel caricamento utente backoffice:', error);
      return null;
    }
  };

  // Funzione per forzare il refresh dei dati backoffice
  const refreshBackofficeUser = async () => {
    if (user?.id) {
      const boUser = await loadBackofficeUser(user.id);
      setBackofficeUser(boUser);
    }
  };

  useEffect(() => {
    let isReady = false;
    let lastUserId: string | null = null;

    const handleAuthSession = async (session: Session | null) => {
      if (!isReady) return;
      setLoading(true);

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user?.id) {
        lastUserId = session.user.id;
        try {
          const boUser = await loadBackofficeUser(session.user.id);
          if (lastUserId !== session.user.id) return;

          if (!boUser) {
            toast.error(
              'Accesso non autorizzato. Il tuo account non ha i permessi per accedere al backoffice.',
            );
            await supabase.auth.signOut();
            setBackofficeUser(null);
            setUser(null);
            setSession(null);
          } else {
            setBackofficeUser(boUser);
          }
        } catch {
          toast.error(
            'Errore nella verifica dei permessi. Per sicurezza sei stato disconnesso.',
          );
          await supabase.auth.signOut();
          setBackofficeUser(null);
          setUser(null);
          setSession(null);
        }
      } else {
        lastUserId = null;
        setBackofficeUser(null);
      }
      setLoading(false);
    };

    const initAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        isReady = true;
        await handleAuthSession(session);
      } catch {
        setLoading(false);
      }
    };

    initAuth().catch((error) => {
      console.error("Errore nell'inizializzazione dell'autenticazione:", error);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isReady) return;
      handleAuthSession(session).catch((error) => {
        console.error(
          'Errore nella gestione del cambiamento di stato di autenticazione:',
          error,
        );
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value: UserContextType = {
    user,
    session,
    backofficeUser,
    loading,
    signIn,
    signOut,
    refreshBackofficeUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
